// components/rapport/reportspages/players/PlayerPositionTable.jsx
import { useMemo, useState, useRef, useEffect } from "react";
import { X } from "lucide-react";
import { usePdfExport } from "../../pdf/PdfExportContext";

function useComboKeys({ open, setOpen, onSelect, items }) {
  const [idx, setIdx] = useState(-1);
  return {
    idx,
    setIdx,
    onKeyDown: (e) => {
      if (!open) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setIdx((i) => Math.min(items.length - 1, i + 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setIdx((i) => Math.max(0, i - 1));
      } else if (e.key === "Enter" && idx >= 0 && items[idx]) {
        e.preventDefault();
        onSelect(items[idx]);
        setOpen(false);
      } else if (e.key === "Escape") {
        e.preventDefault();
        setOpen(false);
      }
    },
  };
}

export function SearchPicker({
  inputClass,
  panelClass,
  placeholder = "",
  items = [],
  renderItem,
  onSelect,
  /** nouveau: n’afficher la liste qu’à partir de ce nombre de caractères */
  minChars = 1,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  const { idx, setIdx, onKeyDown } = useComboKeys({ open, setOpen, onSelect, items });

  useEffect(() => {
    function onClickAway(e) {
      if (!ref.current) return;
      if (!ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onClickAway);
    return () => document.removeEventListener("mousedown", onClickAway);
  }, []);

  // Ouvrir/fermer selon la longueur de la requête
  useEffect(() => {
    setIdx(-1);
    setOpen(q.trim().length >= minChars && items.length > 0);
  }, [q, items, minChars, setIdx]);

  return (
    <div ref={ref} className="relative">
      <input
        className={inputClass}
        placeholder={placeholder}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onKeyDown={onKeyDown}
        aria-expanded={open}
        aria-controls="player-search-panel"
        aria-autocomplete="list"
      />

      {open && (
        <div id="player-search-panel" className={panelClass} role="listbox">
          {items.length === 0 ? (
            <div className="px-3 py-2 text-sm opacity-70">No player...</div>
          ) : (
            items.slice(0, 50).map((it, i) => (
              <button
                key={it.id}
                role="option"
                aria-selected={i === idx}
                className={`w-full text-left px-3 py-2 hover:bg-sky-500/10 ${i === idx ? "bg-sky-500/10" : ""}`}
                onMouseEnter={() => setIdx(i)}
                onClick={() => {
                  onSelect(it);
                  setQ("");           // on vide l’input
                  setOpen(false);     // on ferme la liste
                }}
              >
                {renderItem(it)}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Props:
 * - players: [{id,name,position,avatar?}]
 * - positions: ["Defense", "Midfield", ...]
 * - values: { [playerId]: { metricKey: number|null } }          // current
 * - prevValues: { [playerId]: { metricKey: number|null } }      // previous
 * - byPos: { [position]: { stats: { [metricKey]: { p10, p90, mean, sd } } } }
 * - metrics: [{key,label,isPct?}]
 * - dark: boolean
 */
export default function PlayerPositionTable({
  players = [],
  positions = [],
  values = {},
  prevValues = {},
  byPos = {},
  metrics = [],
  dark = false,
}) {
  const [activePositions, setActivePositions] = useState(new Set(positions));
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState([]);
  const exporter = usePdfExport();

  const addPlayer = (id) => setSelected((s) => (s.includes(id) ? s : [...s, id]));
  const removePlayer = (id) => setSelected((s) => s.filter((x) => x !== id));

  const filteredCandidates = useMemo(() => {
    const q = query.trim().toLowerCase();
    return players
      .filter((p) => activePositions.has(p.position || "N/A"))
      .filter((p) => !q || p.name.toLowerCase().includes(q) || (p.position || "").toLowerCase().includes(q))
      .filter((p) => !selected.includes(p.id));
  }, [players, activePositions, query, selected]);

  const selectedPlayers = useMemo(
    () => selected.map((id) => players.find((p) => p.id === id)).filter(Boolean),
    [selected, players]
  );

  const clr = {
    card: dark ? "bg-gray-900 text-gray-100" : "bg-white text-gray-900",
    border: dark ? "border-gray-700" : "border-gray-200",
    sub: dark ? "text-gray-300" : "text-gray-600",
    chipOn: "bg-sky-600 text-white border-sky-600",
    chipOff: dark ? "bg-gray-800 border-gray-700 text-gray-300" : "bg-gray-100 border-gray-300 text-gray-700",
    input: dark ? "bg-gray-800 border-gray-700 text-gray-100" : "bg-white border-gray-300 text-gray-900",
    band: "bg-sky-200/30",
    marker: "bg-emerald-500",
    up: "text-emerald-600",
    down: "text-rose-600",
    flat: "text-gray-500",
  };

  useEffect(() => {
    // Prépare colonnes
    const columns = metrics.map((m) => ({ key: m.key, label: m.label, isPct: !!m.isPct }));

    // Prépare lignes pour PDF
    const rows = selectedPlayers.map((p) => {
      const posStats = byPos[p.position]?.stats || {};
      const cells = metrics.map((m) => {
        const v = values[p.id]?.[m.key];
        const pv = prevValues[p.id]?.[m.key];
        const st = posStats[m.key];
        const delta = (Number.isFinite(v) && Number.isFinite(pv)) ? (v - pv) : null;
        const z = (st && Number.isFinite(st.sd) && st.sd > 0 && Number.isFinite(v))
          ? (v - st.mean) / st.sd
          : null;
        return {
          key: m.key,
          value: Number.isFinite(v) ? v : null,
          prev: Number.isFinite(pv) ? pv : null,
          delta,
          z,
          p10: (st && Number.isFinite(st.p10)) ? st.p10 : null,
          p90: (st && Number.isFinite(st.p90)) ? st.p90 : null,
        };
      });
      return { id: p.id, name: p.name, position: p.position, avatar: p.avatar || null, cells };
    });

    exporter.setData("players.table", { columns, rows });
  }, [selectedPlayers, metrics, values, prevValues, byPos, exporter]);

  return (
    <div className={`rounded-2xl border ${clr.border} ${clr.card}`}>
        {/* Controls */}
        <div className="p-3 flex flex-col md:flex-row gap-3 md:items-center">
        <div className="flex flex-wrap gap-1">
            {positions.map((pos) => {
            const on = activePositions.has(pos);
            return (
                <button
                key={pos}
                className={`px-2 py-1 text-xs rounded border ${on ? clr.chipOn : clr.chipOff}`}
                onClick={() =>
                    setActivePositions((s) => {
                    const n = new Set(s);
                    n.has(pos) ? n.delete(pos) : n.add(pos);
                    return n;
                    })
                }
                >
                {pos}
                </button>
            );
            })}
        </div>

        {/* À droite : au choix, select natif OU recherche */}
        <div className="md:ml-auto flex items-center gap-2 w-full md:w-auto">

            {/* SELECT natif qui n’affiche rien tant qu’on ne clique pas */}
            <select
            className={`px-2 py-1 rounded border text-sm ${clr.input} w-full md:w-[280px]`}
            value=""
            onChange={(e) => {
                const id = e.target.value;
                if (id) addPlayer(id);
                // reviens sur placeholder
                e.target.value = "";
            }}
            >
            <option value="">Add player…</option>
            {filteredCandidates.map((p) => (
                <option key={p.id} value={p.id}>
                {p.name} — {p.position}
                </option>
            ))}
            </select>

            {/* OU recherche (liste masquée par défaut grâce à minChars=1) */}
            <div className="w-full md:w-[320px] relative hidden md:block">
            <SearchPicker
                inputClass={`px-2 py-1 rounded border text-sm w-full ${clr.input}`}
                panelClass={`absolute z-20 mt-1 w-full rounded-lg border ${clr.border} ${clr.card} shadow-lg max-h-64 overflow-auto`}
                placeholder="Search player…"
                items={filteredCandidates}
                renderItem={(p) => (
                <div className="flex items-center gap-2">
                    <Avatar src={p.avatar} alt={p.name} />
                    <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{p.name}</div>
                    <div className={`text-xs ${clr.sub}`}>{p.position}</div>
                    </div>
                    <span className="text-xs px-2 py-0.5 rounded border">{p.position}</span>
                </div>
                )}
                onSelect={(p) => addPlayer(p.id)}
                minChars={1}
            />
            </div>
        </div>
        </div>

      {/* Header */}
      <div className={`px-3 py-2 border-t ${clr.border} ${dark ? "bg-gray-800" : "bg-gray-50"}`}>
        <div className="grid grid-cols-12 gap-3 items-center">
          <div className="col-span-4 text-xs uppercase tracking-wide">Player</div>
          <div className="col-span-8">
            <div className="grid" style={{ gridTemplateColumns: `repeat(${metrics.length}, minmax(0,1fr))` }}>
              {metrics.map((m) => (
                <div key={m.key} className="text-xs uppercase tracking-wide truncate text-right">
                  {m.label}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Rows */}
      {selectedPlayers.length === 0 ? (
        <div className="p-6 text-sm opacity-75">Select players to compare…</div>
      ) : (
        <div className="divide-y" style={{ borderColor: dark ? "#374151" : "#E5E7EB" }}>
          {selectedPlayers.map((p) => (
            <PlayerRow
              key={p.id}
              player={p}
              metrics={metrics}
              statsByPos={byPos[p.position]?.stats || {}}
              curValues={values[p.id] || {}}
              prevValues={prevValues[p.id] || {}}
              dark={dark}
              onRemove={() => removePlayer(p.id)}
              colors={clr}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function Avatar({ src, alt }) {
  return (
    <div className="w-10 h-10 rounded-lg overflow-hidden bg-gray-200 flex items-center justify-center">
      {src ? <img src={src} alt={alt} className="w-full h-full object-cover" /> : <span className="text-[10px] opacity-60">N/A</span>}
    </div>
  );
}

function PlayerRow({ player, metrics, statsByPos, curValues, prevValues, dark, onRemove, colors }) {
  return (
    <div className="px-3 py-3">
      <div className="grid grid-cols-12 gap-3 items-center">
        {/* Left: identity */}
        <div className="col-span-4">
          <div className="flex items-center gap-3">
            <Avatar src={player.avatar} alt={player.name} />
            <div className="min-w-0">
              <div className="font-medium truncate">{player.name}</div>
              <div className="flex items-center gap-2">
                <span className={`px-2 py-0.5 text-xs rounded-full border ${dark ? "border-gray-600 text-gray-200" : "border-gray-300 text-gray-800"}`}>
                  {player.position}
                </span>
              </div>
            </div>
            <button className="ml-auto p-1 rounded hover:bg-rose-500/10" onClick={onRemove} title="Remove">
              <X className="w-4 h-4 opacity-70" />
            </button>
          </div>
        </div>

        {/* Right: metric micro-viz + delta */}
        <div className="col-span-8">
            <div
                className="grid gap-3 items-stretch"
                style={{ gridTemplateColumns: `repeat(${metrics.length}, minmax(0,1fr))` }}
            >
                {metrics.map((m) => (
                <MetricCell
                    key={m.key}
                    value={curValues?.[m.key]}
                    prev={prevValues?.[m.key]}
                    stat={statsByPos?.[m.key]}
                    isPct={!!m.isPct}
                    colors={colors}
                />
                ))}
            </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Mini-cell:
 * - fond = bande P10–P90 (poste) – SANS médiane (supprimée)
 * - point = valeur joueur (courante)
 * - à droite: valeur formatée + delta (↑/↓ avec couleur)
 */
function MetricCell({ value, prev, stat, isPct, colors }) {
  const hasBand =
    !!stat &&
    Number.isFinite(stat.p10) &&
    Number.isFinite(stat.p90) &&
    stat.p90 > stat.p10;

  const lo = hasBand ? stat.p10 : null;
  const hi = hasBand ? stat.p90 : null;

  // Affichage valeur
  const fmtVal = (v) => {
    if (v == null || !Number.isFinite(v)) return "—";
    return isPct ? `${(v * 100).toFixed(1)}%` : v.toFixed(2);
  };

  // Delta & tendance
  const delta =
    Number.isFinite(value) && Number.isFinite(prev) ? value - prev : null;
  const eps = isPct ? 0.001 : 0.005;
  const trend =
    delta == null ? "flat" : delta > eps ? "up" : delta < -eps ? "down" : "flat";
  const dTxt =
    delta == null
      ? ""
      : isPct
      ? `${(Math.abs(delta) * 100).toFixed(1)}%`
      : Math.abs(delta).toFixed(2);

  // Projection sur la barre : P10=10%, P90=90%
  const LEFT = 10;
  const RIGHT = 90;
  let px = null;
  let inRange = false;

  if (hasBand && value != null) {
    const t = (value - lo) / (hi - lo); // 0..1 si dans [P10,P90]
    // position théorique (peut être <0 ou >1 si hors-borne)
    const pos = LEFT + t * (RIGHT - LEFT);
    px = Math.max(-2, Math.min(102, pos));
    inRange = pos >= LEFT && pos <= RIGHT;
  }

  const dotClass = !hasBand || px == null
    ? "bg-gray-400"
    : inRange
    ? colors.marker           // vert
    : (value < lo ? "bg-amber-500" : "bg-rose-500"); // sous / sur

  return (
    <div className="flex flex-col items-stretch gap-1">
      {/* Valeur + delta au-dessus */}
      <div className="flex items-center justify-between text-xs tabular-nums">
        <div className="font-medium">{fmtVal(value)}</div>
        <div
          className={`${
            trend === "up" ? colors.up : trend === "down" ? colors.down : colors.flat
          } flex items-center gap-1`}
          title={delta == null ? "" : `Δ ${dTxt}`}
        >
          {trend === "up" ? "▲" : trend === "down" ? "▼" : "·"}
          <span>{dTxt}</span>
        </div>
      </div>

      {/* Barre : axe + bande P10–P90 + ticks + point */}
      <div className="relative h-5 rounded bg-gray-200/60 overflow-hidden">
        {/* Bande P10–P90 (uniquement la zone centrale) */}
        {hasBand && (
          <div
            className="absolute top-0 bottom-0"
            style={{
              left: `${LEFT}%`,
              width: `${RIGHT - LEFT}%`,
              background: "rgba(14,165,233,0.18)",
            }}
            title="Plage typique du poste (P10–P90)"
          />
        )}

        {/* Ticks P10/P90 */}
        {hasBand && (
          <>
            <div
              className="absolute top-0 bottom-0 w-0.5"
              style={{ left: `${LEFT}%`, background: "rgba(59,130,246,0.85)" }}
              title="P10"
            />
            <div
              className="absolute top-0 bottom-0 w-0.5"
              style={{ left: `${RIGHT}%`, background: "rgba(59,130,246,0.85)" }}
              title="P90"
            />
          </>
        )}

        {/* Point valeur */}
        {px != null && (
          <div
            className={`absolute top-1/2 -translate-y-1/2 w-2 h-2 rounded-full ${dotClass}`}
            style={{ left: `calc(${px}% - 4px)` }}
            title="Valeur du joueur"
          />
        )}
      </div>
    </div>
  );
}
